'use client';

import { isLangSlug } from '@shared/utils/isLangSlug';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

const isPlainLeftClick = (event: MouseEvent) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Menu links are authored without a language slug (`/membership-benefits`) while the rendered URL
 * carries one (`/en-GB/membership-benefits`), so both have to be normalised before comparing.
 */
const toComparableRoute = (pathname: string) => {
    const segments = pathname.split('/').filter(Boolean);

    if (isLangSlug(segments[0])) {
        segments.shift();
    }

    return `/${segments.join('/')}`;
};

/**
 * The App Router only scrolls the route segment that changed into view. A link pointing at the
 * route you are already on changes nothing, so the router does not scroll — every header, mega menu
 * and footer menu repeats the current page's own link, which leaves those clicks doing nothing
 * visible halfway down a page. Same for a target segment that already starts inside the viewport.
 *
 * Only link-initiated navigations are handled, so `router.push(…, { scroll: false })` flows
 * (airport guide tabs, the billing/3DS journeys) keep their scroll position.
 */
const ScrollToTopOnNavigation: React.FC = () => {
    const pathname = usePathname();
    const isNavigationFromLink = useRef(false);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            isNavigationFromLink.current = false;

            if (event.defaultPrevented || !isPlainLeftClick(event)) {
                return;
            }

            const target = event.target instanceof Element ? event.target : null;
            const anchor = target?.closest('a[href]');

            if (
                !(anchor instanceof HTMLAnchorElement) ||
                anchor.target === '_blank' ||
                anchor.hasAttribute('download')
            ) {
                return;
            }

            const destination = new URL(anchor.href, window.location.href);

            if (destination.hash || destination.origin !== window.location.origin) {
                return;
            }

            if (
                toComparableRoute(destination.pathname) ===
                toComparableRoute(window.location.pathname)
            ) {
                window.scrollTo({ top: 0, behavior: 'instant' });
                return;
            }

            isNavigationFromLink.current = true;
        };

        document.addEventListener('click', handleClick, true);

        const cancel = () => {
            isNavigationFromLink.current = false;
        };
        window.addEventListener('popstate', cancel);

        return () => {
            document.removeEventListener('click', handleClick, true);
            window.removeEventListener('popstate', cancel);
        };
    }, []);

    useEffect(() => {
        if (!isNavigationFromLink.current) {
            return;
        }

        isNavigationFromLink.current = false;
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);

    return null;
};

export default ScrollToTopOnNavigation;
